import { Card, Grid2, IconButton, Stack, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { IPlaceSchedule } from "../../../../../Domain/Types/FloorPlan/PlaceSchedule";
import { handlePermChange } from "../../../Partial/PermissionChange.partial";
import { PermissionSelectGroup } from "../../../Partial/PermissionSelectGroup.partial";
import {
  RemoteCompanyPerm,
  RemoteCompanyUnitsPerm,
  RemoteUsersPerm
} from "../../../../../Domain/Types/FloorPlan/Permission.type";
import { generateNewPerms } from "./PermissionDialogContent.partial";
import { initPerms } from "../../functions/initPerms.function";
import { NewPerms } from "../../../../Views/CreateFloorPlanView/Tools/useFloorplan/useFloorplan";
import { RemoteCompany } from "../../../../../../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { CompanyUnits } from "../../../../../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { Colleague } from "../../../../../../../features/Connections/types/Colleague.type";

type Props = {
  selectedPlaces: IPlaceSchedule[];
  remoteUnits: {
    remoteCompanies: RemoteCompany[] | undefined;
    remoteCompanyUnits: CompanyUnits[] | undefined;
    remoteUsers: Colleague[] | undefined;
  };
  isRemoteUnitsFetching: boolean;
  isEditable: boolean;
  onChangePerm: (perms: NewPerms) => void;
  onClose: () => void;
};

export function DeskPermissionDialogContent({
  selectedPlaces,
  remoteUnits,
  isRemoteUnitsFetching,
  isEditable,
  onChangePerm,
  onClose
}: Readonly<Props>) {
  const { t } = useTranslation();

  const { initialPermCompanies, initialPermCompanyUnits, initialPermUsers } =
    initPerms(selectedPlaces);

  return (
    <Card sx={{ p: 2, maxHeight: "70%", overflowY: "visible", width: "25vw" }}>
      <Grid2 container>
        <Grid2 size={"grow"} />

        {/* title of permission modal */}
        <Grid2 size={{ xs: 9 }} sx={{ alignSelf: "center" }}>
          <Typography align={"center"} lineHeight={2.25}>
            {t("Adjust permissions")}
          </Typography>
        </Grid2>

        <Grid2 size={"grow"} sx={{ textAlign: "right" }}>
          <IconButton data-testid="btn-group-close" onClick={onClose} size="large">
            <Close />
          </IconButton>
        </Grid2>
      </Grid2>

      <Stack direction={"column"} gap={4}>
        <PermissionSelectGroup
          initialPermCompanies={initialPermCompanies}
          initialPermCompanyUnits={initialPermCompanyUnits}
          initialPermUsers={initialPermUsers}
          type={"place"}
          isUsingTimeRelatedPerm
          handlePermChange={handlePermChange}
          placeUpdatePerms={(companies, units, users) =>
            handlePlaceUpdatePerms(onClose, onChangePerm, companies, units, users)
          }
          remoteUnits={remoteUnits}
          isRemoteUnitsFetching={isRemoteUnitsFetching}
          isEditable={isEditable}
        />
      </Stack>
    </Card>
  );
}

export function handlePlaceUpdatePerms(
  onClose: () => void,
  onChangePerm: (perms: NewPerms) => void,
  selectedCompanies: RemoteCompanyPerm[] = [],
  selectedCompanyUnits: RemoteCompanyUnitsPerm[] = [],
  selectedUsers: RemoteUsersPerm[] = []
) {
  const newPerms = generateNewPerms({ selectedCompanies, selectedCompanyUnits, selectedUsers });

  onChangePerm(newPerms);
  onClose();
}

export function singlePlaceName(
  showMultiPlacesEdit: boolean,
  selectedPlaces: IPlaceSchedule[],
  t: TFunction
) {
  if (!showMultiPlacesEdit) {
    if (selectedPlaces[0]?.inventory?.name) return `${selectedPlaces[0].inventory.name}`;
    return t("New Place");
  }
}
